import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, Alert, TablePagination, Typography, Paper } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ApiManagerFilters from "../../../components/Admin/ApiManager/ApiManagerFilters";
import ApiManagerTable from "../../../components/Admin/ApiManager/ApiManagerTable";
import ApiManagerForm from "../../../components/Admin/ApiManager/ApiManagerForm";
import { useMemo, useState } from "react";
import { useAuth } from "../../../auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getListApiManagerQueryKey,
  getListApiManagerQueryOptions,
  getCreateApiManagerMutationOptions,
  getUpdateApiManagerMutationOptions,
  getDeleteApiManagerMutationOptions,
  getExecuteApiManagerMutationOptions,
} from "../../../api/bpm/endpoints";
import type { ApiManagerDto, ApiManagerInput } from "../../../api/bpm/model";
import { useTranslation } from "react-i18next";

const apiManagerSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
});

export const Route = createFileRoute('/__auth/admin/api-manager')({
  component: ApiManagerComponent,
  validateSearch: (search) => apiManagerSearchSchema.parse(search),
  staticData: {
    title: "admin.apiManager.title",
  },
});

function ApiManagerComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedApi, setSelectedApi] = useState<ApiManagerDto | null>(null);
  const [apiToDelete, setApiToDelete] = useState<ApiManagerDto | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [executionResult, setExecutionResult] = useState<any>(null);
  const [isResultDialogOpen, setIsExecutionResultDialogOpen] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const { currentTenant } = useAuth();
  const effectiveTenant = currentTenant || "default-domain";
  const apiManagerQueryKey = getListApiManagerQueryKey(effectiveTenant);

  const refreshApiManager = () =>
    queryClient.invalidateQueries({
      queryKey: apiManagerQueryKey,
    });

  const { data, isLoading, error } = useQuery({
    ...getListApiManagerQueryOptions(
      effectiveTenant,
      {
                page: (search.page || 1) - 1,
                size: search.size || 10,
                q: search.q || undefined
          } as any
    ),
    staleTime: 30000,
  });

  const rows: ApiManagerDto[] = useMemo(() => {
    if (!data) return [];
    if ('content' in data) return (data as any).content;
    if (Array.isArray(data)) return data;
    return [];
  }, [data]);

  const totalElements = useMemo(() => {
    if (!data) return 0;
    if ('totalElements' in data && typeof (data as any).totalElements === 'number') {
      return (data as any).totalElements;
    }
    return rows.length;
  }, [data, rows.length]);

  const handlePageChange = (_: unknown, value: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilter = (newFilters: { q?: string }) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: 1
      }),
    });
  };

  const createMutation = useMutation({
    ...getCreateApiManagerMutationOptions(),
    onSuccess: () => {
      refreshApiManager();
      showSnackbar(t("admin.apiManager.createdSuccess"));
      setIsDialogOpen(false);
    },
    onError: () => showSnackbar(t("admin.apiManager.savedError"), "error"),
  });

  const updateMutation = useMutation({
    ...getUpdateApiManagerMutationOptions(),
    onSuccess: () => {
      refreshApiManager();
      showSnackbar(t("admin.apiManager.updatedSuccess"));
      setIsDialogOpen(false);
    },
    onError: () => showSnackbar(t("admin.apiManager.savedError"), "error"),
  });

  const deleteMutation = useMutation({
    ...getDeleteApiManagerMutationOptions(),
    onSuccess: () => {
      refreshApiManager();
      showSnackbar(t("admin.apiManager.deletedSuccess"));
    },
  });

  const executeMutation = useMutation({
    ...getExecuteApiManagerMutationOptions(),
    onSuccess: (res) => {
      setExecutionResult(res);
      setIsExecutionResultDialogOpen(true);
    },
    onError: (err: any) => {
      setExecutionResult(err?.body || err);
      setIsExecutionResultDialogOpen(true);
    },
  });

  return (
    <ListLayout
      title={t("admin.apiManager.title")}
      subtitle={t("admin.apiManager.subtitle")}
      filters={
        <ApiManagerFilters
          onApply={handleFilter}
          isLoading={isLoading}
          initialValues={{ q: search.q }}
        />
      }
      isLoading={isLoading}
      isEmpty={!rows.length}
      error={error as any}
      actions={
        <IconButton color="primary" onClick={() => { setSelectedApi(null); setIsDialogOpen(true); }}>
          <AddIcon />
        </IconButton>
      }
    >
      <ApiManagerTable
        data={rows}
        isLoading={isLoading}
        onEdit={(item) => { setSelectedApi(item); setIsDialogOpen(true); }}
        onDelete={(item) => { setApiToDelete(item); setIsDeleteDialogOpen(true); }}
        onExecute={(item) => executeMutation.mutate({ tenant: effectiveTenant, id: item.id!, data: {} })}
      />

      <TablePagination
        component="div"
        count={totalElements}
        page={(search.page || 1) - 1}
        onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          navigate({
            search: (prev) => ({ ...prev, size: newSize, page: 1 }),
            replace: true
          });
        }}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) => `${t("common.showing")} ${from}-${to} ${t("common.of")} ${count}`}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      />

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <ApiManagerForm
            initialValues={selectedApi || undefined}
            onSubmit={(vals) => {
              if (selectedApi?.id) {
                updateMutation.mutate({ tenant: effectiveTenant, id: selectedApi.id, data: vals as unknown as ApiManagerInput });
              } else {
                createMutation.mutate({ tenant: effectiveTenant, data: vals as unknown as ApiManagerInput });
              }
            }}
            onCancel={() => setIsDialogOpen(false)}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {t("admin.apiManager.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("admin.apiManager.confirmDelete", { name: apiToDelete?.name })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (apiToDelete?.id) {
                deleteMutation.mutate({ tenant: effectiveTenant, id: apiToDelete.id });
              }
              setIsDeleteDialogOpen(false);
              setApiToDelete(null);
            }}
            variant="contained"
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isResultDialogOpen} onClose={() => setIsExecutionResultDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <Typography variant="h6" gutterBottom>{t("admin.apiManager.executionResult")}</Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', overflow: 'auto', maxHeight: 400 }}>
            <pre>{JSON.stringify(executionResult, null, 2)}</pre>
          </Paper>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={() => setIsExecutionResultDialogOpen(false)}>{t("common.close")}</IconButton>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </ListLayout>
  );
}
